import {
  AllergyIntolerance,
  Coding,
  Condition,
  DiagnosticReport,
  Immunization,
  Observation,
  Procedure,
} from "@medplum/fhirtypes";
import { EhrWritePath } from "../../../../domain/ehr";
import {
  isAllergyIntolerance,
  isCondition,
  isConditionSeries,
  isDiagnosticReport,
  isImmunization,
  isImmunizationSeries,
  isMedicationSeries,
  isObservation,
  isObservationSeries,
  isProcedure,
} from "./type-guards";
import { CurrentEhr } from "../../shared-logic/ehr-context/ehr-context";
import { MedicationWithRefs } from "../../shared-components/fhir-sections/medications/table-data";
import { getValueAndInterpretation } from "../../shared-components/fhir-sections/labs/shared";

/**
 * EHR WRITE-BACK VALIDATOR (Logic copied from V1)
 *
 * Centralized validation logic for determining if a resource can be written to an EHR.
 * Each EHR has specific requirements (code allowlists, required fields, etc.)
 */

// Code system constants (copied from V1 shared.ts)
const LOINC_CODE = "loinc";
const CPT_CODE = "cpt";
const CVX_CODE = "cvx";
const RX_NORM_CODE = "rxnorm";
const SNOMED_CODE = "snomed";
const ICD_10_CODE = "icd-10";

const UNKNOWN_DISPLAY = "Unknown";
const UNK_CODE = "UNK";

type CodeMap = {
  system: string;
  code: string;
  display: string;
};

/**
 * Helper to find the first coding that matches any of the specified systems
 * Uses substring matching (e.g., "loinc" matches "http://loinc.org")
 * COPIED FROM V1 shared.ts
 */
function getFirstCodeSpecified(
  coding: Coding[] | undefined,
  systemsList: string[]
): CodeMap | undefined {
  let specifiedCode: CodeMap | undefined = undefined;

  if (systemsList.length && coding) {
    for (const system of systemsList) {
      let matchedCoding: Coding | undefined;
      for (const codingEntry of coding) {
        const lowerCaseSystem = codingEntry.system?.toLowerCase();
        if (lowerCaseSystem?.includes(system) && codingEntry.code) {
          matchedCoding = codingEntry;
          break;
        }
      }

      if (matchedCoding) {
        specifiedCode = {
          system: matchedCoding.system ?? UNK_CODE,
          code: matchedCoding.code ?? UNKNOWN_DISPLAY,
          display: matchedCoding.display ?? UNKNOWN_DISPLAY,
        };
        break;
      }
    }
  }

  return specifiedCode;
}

// Athena vital LOINC codes allowlist
const VITAL_CODES_ATHENA = [
  "8310-5", // Body temperature
  "8867-4", // Heart rate
  "9279-1", // Respiratory rate
  "2708-6", // Oxygen saturation
  "8462-4", // Diastolic BP
  "8480-6", // Systolic BP
  "29463-7", // Body weight
  "8302-2", // Body height
  "39156-5", // BMI
];

// Canvas vital LOINC codes allowlist
const VITAL_CODES_CANVAS = [
  "85354-9", // Blood pressure panel
  "29463-7", // Body weight
  "8302-2", // Body height
  "8867-4", // Heart rate
  "8310-5", // Body temperature
  "2708-6", // Oxygen saturation
  "59408-5", // Oxygen saturation in Arterial blood by Pulse oximetry
  "9279-1", // Respiratory rate
  "56086-2", // Body Mass Index
];

type ValidationResult = {
  isValid: boolean;
  reason?: string;
};

// Generic user-friendly messages
const GENERIC_ERROR_MESSAGE = "Cannot write to EHR";

/**
 * Main validator function - routes to specific validators based on path and EHR
 */
export function validateEhrWriteBack(
  originalData: unknown,
  path: EhrWritePath,
  currentEhr: CurrentEhr
): ValidationResult {
  switch (path) {
    case "vitals":
      return validateVital(originalData, currentEhr);
    case "lab":
      return validateLab(originalData);
    case "condition":
      return validateCondition(originalData, currentEhr);
    case "immunization":
      return validateImmunization(originalData, currentEhr);
    case "medication":
      return validateMedication(originalData, currentEhr);
    case "procedure":
      return validateProcedure(originalData);
    case "allergy":
      return validateAllergy(originalData, currentEhr);
    case "note":
      return validateNote(originalData);
    default:
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }
}

// ============================================================================
// VITALS VALIDATION
// ============================================================================

function validateVital(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const observation = extractObservation(data);
  if (!observation) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // Get LOINC code using V1 logic
  const loincCoding = getFirstCodeSpecified(observation.code?.coding ?? [], [LOINC_CODE]);

  if (!loincCoding) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // EHR-specific validation (matches V1 exactly)
  if (currentEhr.isAthena) {
    if (!VITAL_CODES_ATHENA.includes(loincCoding.code)) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  } else if (currentEhr.isCanvas) {
    if (!loincCoding.code) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    if (!loincCoding.display) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    if (!VITAL_CODES_CANVAS.includes(loincCoding.code)) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    const status = observation.status;
    if (!status) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  // Check for units (matches V1 exactly)
  const normalizedUnit = observation.valueQuantity?.unit?.replace(/[{()}]/g, "");
  const units = normalizedUnit?.toLowerCase();
  if (!units) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  return { isValid: true };
}

// ============================================================================
// LAB VALIDATION
// ============================================================================

function validateLab(data: unknown): ValidationResult {
  const lab = extractObservation(data);
  if (!lab) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 lab validation logic
  const loincCoding = getFirstCodeSpecified(lab.code?.coding ?? [], [LOINC_CODE]);
  if (!loincCoding) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // Use shared function for value extraction (includes blacklist filtering)
  const { value, unit, referenceRange } = getValueAndInterpretation(lab);

  if (!unit) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // Check for value - properly handle zero values (don't reject 0)
  if (value === undefined || value === null || value === "") {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  if (!referenceRange) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const resultStatus = lab.status;
  if (!resultStatus) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const date = lab.effectiveDateTime || lab.effectivePeriod?.start;
  if (!date) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  return { isValid: true };
}

// ============================================================================
// CONDITION VALIDATION
// ============================================================================

function validateCondition(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const condition = extractCondition(data);
  if (!condition) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 condition validation logic
  if (currentEhr.isElation || currentEhr.isAthena) {
    const snomedCoding = getFirstCodeSpecified(condition.code?.coding ?? [], [SNOMED_CODE]);
    if (!snomedCoding) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  } else if (currentEhr.isCanvas) {
    const icd10Coding = getFirstCodeSpecified(condition.code?.coding ?? [], [ICD_10_CODE]);
    if (!icd10Coding) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    if (!icd10Coding.code) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    if (!icd10Coding.display) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  const startDate = condition.onsetDateTime ?? condition.onsetPeriod?.start;
  if (!startDate) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  if (currentEhr.isElation || currentEhr.isCanvas) {
    // Check clinicalStatus as proxy for condition status
    const status = condition.clinicalStatus?.coding?.[0]?.code;
    if (!status) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  return { isValid: true };
}

// ============================================================================
// IMMUNIZATION VALIDATION
// ============================================================================

function validateImmunization(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const immunization = extractImmunization(data);
  if (!immunization) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 immunization validation logic
  const cvxCoding = getFirstCodeSpecified(immunization.vaccineCode?.coding ?? [], [CVX_CODE]);
  if (!cvxCoding) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const date = immunization.occurrenceDateTime || immunization.occurrenceString;
  if (!date) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  if (currentEhr.isCanvas) {
    if (!cvxCoding.code) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    if (!cvxCoding.display) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    const status = immunization.status;
    if (!status) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  return { isValid: true };
}

// ============================================================================
// MEDICATION VALIDATION
// ============================================================================

function validateMedication(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  // Extract MedicationWithRefs from V2 data structures
  let medicationWithRefs: MedicationWithRefs | undefined;

  // Check if it's EnhancedMedicationSeries
  if (typeof data === "object" && data !== null && "medicationWithRefs" in data && "code" in data) {
    medicationWithRefs = data.medicationWithRefs as MedicationWithRefs;
  } else if (isMedicationSeries(data)) {
    // Get most recent from series
    medicationWithRefs = data.resources[data.resources.length - 1];
  } else {
    // Might be raw MedicationWithRefs
    medicationWithRefs = data as MedicationWithRefs;
  }

  if (!medicationWithRefs) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 medication validation logic
  if (!medicationWithRefs.statement || medicationWithRefs.statement.length < 1) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const startDates = medicationWithRefs.statement.flatMap(statement => {
    const startDate = statement.effectiveDateTime ?? statement.effectivePeriod?.start;
    if (!startDate) return [];
    return [startDate];
  });

  if (startDates.length < 1) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const medication = medicationWithRefs.medication;
  const rxNormCoding = getFirstCodeSpecified(medication?.code?.coding ?? [], [RX_NORM_CODE]);
  if (!rxNormCoding) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  if (currentEhr.isCanvas) {
    if (!rxNormCoding.display) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    const statusesAndDosagesExist = medicationWithRefs.statement.find(statement => {
      const status = statement.status;
      if (!status) return false;
      const dosageText = statement.dosage?.[0]?.text;
      if (!dosageText) return false;
      return true;
    });
    if (!statusesAndDosagesExist) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  return { isValid: true };
}

// ============================================================================
// PROCEDURE VALIDATION
// ============================================================================

function validateProcedure(data: unknown): ValidationResult {
  const procedure = extractProcedure(data);
  if (!procedure) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 procedure validation logic
  const cptCoding = getFirstCodeSpecified(procedure.code?.coding ?? [], [CPT_CODE]);
  if (!cptCoding) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const date = procedure.performedDateTime || procedure.performedPeriod?.start;
  if (!date) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  return { isValid: true };
}

// ============================================================================
// ALLERGY VALIDATION
// ============================================================================

function validateAllergy(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const allergyIntolerance = extractAllergy(data);
  if (!allergyIntolerance) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // V1 allergy validation logic
  const reaction = allergyIntolerance.reaction;
  if (!reaction || reaction.length < 1) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const codingsPairs: [CodeMap, CodeMap | undefined][] = reaction.flatMap(r => {
    const substanceRxnormCoding = getFirstCodeSpecified(r.substance?.coding ?? [], [RX_NORM_CODE]);
    if (!substanceRxnormCoding) return [];
    const manifestationSnomedCoding = getFirstCodeSpecified(
      r.manifestation?.flatMap(m => m.coding ?? []),
      [SNOMED_CODE]
    );
    if (currentEhr.isAthena && !manifestationSnomedCoding) return [];
    return [[substanceRxnormCoding, manifestationSnomedCoding]];
  });

  if (codingsPairs.length < 1) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  const onset = allergyIntolerance.onsetDateTime ?? allergyIntolerance.onsetPeriod?.start;
  if (!onset) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  if (currentEhr.isCanvas) {
    const clinicalStatus = allergyIntolerance.clinicalStatus;
    if (!clinicalStatus) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
    const verificationStatus = allergyIntolerance.verificationStatus;
    if (!verificationStatus) {
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
    }
  }

  return { isValid: true };
}

// ============================================================================
// NOTE VALIDATION
// ============================================================================

function validateNote(data: unknown): ValidationResult {
  const report = extractDiagnosticReport(data);
  if (!report) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  // Check if there's text content to write
  const hasTextContent =
    (report.presentedForm && report.presentedForm.length > 0) || report.conclusion;

  if (!hasTextContent) {
    return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }

  return { isValid: true };
}

// ============================================================================
// EXTRACTION HELPERS - Handle both V2 Series and raw FHIR resources
// ============================================================================

function extractObservation(data: unknown): Observation | undefined {
  if (isObservationSeries(data)) {
    return data.resources[data.resources.length - 1] as Observation;
  }
  if (isObservation(data)) {
    return data as Observation;
  }
  return undefined;
}

function extractCondition(data: unknown): Condition | undefined {
  if (isConditionSeries(data)) {
    return data.resources[data.resources.length - 1] as Condition;
  }
  if (isCondition(data)) {
    return data as Condition;
  }
  return undefined;
}

function extractImmunization(data: unknown): Immunization | undefined {
  if (isImmunizationSeries(data)) {
    return data.resources[data.resources.length - 1] as Immunization;
  }
  if (isImmunization(data)) {
    return data as Immunization;
  }
  return undefined;
}

function extractProcedure(data: unknown): Procedure | undefined {
  if (isProcedure(data)) {
    return data as Procedure;
  }
  return undefined;
}

function extractAllergy(data: unknown): AllergyIntolerance | undefined {
  if (isAllergyIntolerance(data)) {
    return data as AllergyIntolerance;
  }
  return undefined;
}

function extractDiagnosticReport(data: unknown): DiagnosticReport | undefined {
  if (isDiagnosticReport(data)) {
    return data as DiagnosticReport;
  }
  return undefined;
}

// Type guards imported from ./type-guards.ts
